/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Flex, Text } from 'rebass';

// --------------------- TABS ---------------------

export const Tabs = React.forwardRef(function Tabs(props, ref) {
  const {
    centered,
    children: childrenProp,
    color,
    compact,
    fullWidth,
    onChange,
    value,
  } = props;

  let childIndex = 0;
  const children = React.Children.map(childrenProp, child => {
    if (!React.isValidElement(child)) return null;

    const childValue =
      child.props.value === undefined ? childIndex : child.props.value;
    const selected = childValue === value;

    childIndex += 1;

    return React.cloneElement(child, {
      compact,
      fullWidth,
      onChange,
      stretched: false, // 👈 prevent full-width stretch
      activeColor: color,
      index: childIndex,
      active: selected,
      value: childValue,
    });
  });

  return (
    <TabsContainer
      padding="2px"
      role="tablist"
      ref={ref}
      centered={centered}
      fullWidth={fullWidth}
    >
      {children}
    </TabsContainer>
  );
});

Tabs.propTypes = {
  centered: PropTypes.bool,
  children: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  color: PropTypes.string,
  compact: PropTypes.bool,
  fullWidth: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  onChange: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  value: PropTypes.any,
};

Tabs.defaultProps = {
  centered: false,
  children: null,
  compact: false,
  fullWidth: false,
};

// --------------------- STYLED COMPONENTS ---------------------

const TabsContainer = styled(Flex).attrs(({ centered, fullWidth }) => ({
  'data-full-width': fullWidth,
  'data-centered': centered,
}))`
  display: flex;
  overflow-x: auto;
  position: relative;
  user-select: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border || '#E5E7EB'};
  width: 100%;
  justify-content: flex-start; /* 👈 default left alignment */
  gap: 24px; /* consistent spacing between tabs */

  &[data-centered='true'] {
    justify-content: center;
    gap: 32px;
  }

  &[data-full-width='true'] {
    width: 100%;
  }
`;

const TabContainer = styled(Flex).attrs(({ active, stretched }) => ({
  'data-active': active,
  'data-stretched': stretched,
}))`
  flex: 0 0 auto; /* 👈 natural width, no stretch */
  background: none;
  border: none;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 500 : 400)};
  color: ${({ active, theme }) =>
    active ? theme.colors.text : theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.text};
  }

  &[data-active='true']::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -1px;
    height: 2px;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.primary.main || '#9C5FFF'};
    border-radius: 2px;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.disabled || '#A1A1AA'};
    cursor: default;
  }
`;

// --------------------- TAB ---------------------

export const Tab = React.forwardRef(function Tab(props, ref) {
  const {
    active,
    activeColor,
    compact,
    disabled,
    filled,
    icon,
    label,
    onChange,
    onClick,
    stretched,
    value,
  } = props;

  const handleClick = React.useCallback(
    event => {
      if (onClick) onClick(value, event);
      if (onChange) onChange(value, event);
    },
    [onChange, onClick, value],
  );

  return (
    <TabContainer
      as="button"
      role="tab"
      active={active}
      activeColor={activeColor}
      ref={ref}
      onClick={handleClick}
      filled={filled}
      disabled={disabled}
      stretched={stretched}
    >
      {icon && <TabIconContainer>{icon}</TabIconContainer>}
      {compact && !active ? null : <Text>{label}</Text>}
    </TabContainer>
  );
});

Tab.propTypes = {
  active: PropTypes.bool,
  activeColor: PropTypes.string,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
  filled: PropTypes.bool,
  icon: PropTypes.node,
  // eslint-disable-next-line react/require-default-props, react/no-unused-prop-types
  index: PropTypes.number,
  label: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  onChange: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  onClick: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  stretched: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  value: PropTypes.any,
};

Tab.defaultProps = {
  active: false,
  compact: false,
  disabled: false,
  filled: false,
  activeColor: 'primary',
  icon: null,
  label: null,
};

const TabIconContainer = styled.div`
  margin-right: 8px;
  & > svg {
    fill: currentColor;
  }
`;
